import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { MedicalItemScreeningTest, MedicalItemScreeningTestWithLink, ScreeningTestStatus } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalRequestFormRowComponent } from "./MedicalRequestFormRow";
import { ScreeningTestFormComponent } from "./ScreeningTestFormComponent";
import { ScreeningTestViewComponent } from "./ScreeningTestViewComponent";

import stylesImport from "./MedicalRequestPopupController.module.scss";
import legacyStylesImport from "./MedicalRequestPopupControllerLegacy.module.scss";

interface ILinkCareElementComponentProps {
    elements: MedicalItemScreeningTest[];
    linkedElement: MedicalItemScreeningTestWithLink;
    patientDoB: Date;

    showHasNoCareElements?: boolean;
    infoMessage?: string;

    disabledFormFields?: boolean;
    disableSelection?: boolean;

    shouldSetSelectElementToPendingStatus?: boolean;

    onSelectedElementChanged: (selected: MedicalItemScreeningTest) => void;
}

class LinkCareElementComponentState {
    public selectedElement: MedicalItemScreeningTest;
}

class LinkCareElementComponent extends BaseReactComponent<ILinkCareElementComponentProps, LinkCareElementComponentState> {
    private id = "LinkCareElementComponent";
    private styles = cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
        ? stylesImport
        : legacyStylesImport;

    constructor(props: ILinkCareElementComponentProps) {
        super(props);

        const initialState = new LinkCareElementComponentState();
        initialState.selectedElement = props.linkedElement;
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: ILinkCareElementComponentProps): void {
        if (this.props.linkedElement !== prevProps.linkedElement) {
            this.mutateState(s => s.selectedElement = this.props.linkedElement);
        }
    }

    private mutateModelState(mutation: (m: MedicalItemScreeningTest) => void, then?: () => void): void {
        this.mutateState(
            s => {
                mutation(s.selectedElement);
                return s;
            },
            () => {
                this.props.onSelectedElementChanged?.(this.state.selectedElement);
                then?.();
            }
        );
    }

    public render(): JSX.Element {
        const elements = this.props.elements;
        if (!elements)
            return null;

        return (
            <div id={this.id} className={this.styles.linkedCareElementRow}>
                <MedicalRequestFormRowComponent
                    label="LinkedCareElementLabel"
                    dataCy="linkedCareSectionRow"
                    className={this.styles.requestStatusHeader}
                />
                {this.props.showHasNoCareElements
                    ? this.renderNoElement()
                    : this.renderBody(elements)
                }
            </div>
        );
    }

    private renderNoElement(): JSX.Element {
        return (
            <p style={{ textAlign: "center", fontStyle: "italic" }}>{cacheStore.GetString("NoLinkableCareElementsLabel")}</p>
        );
    }

    private renderBody(elements: MedicalItemScreeningTest[]): JSX.Element[] {
        const render: JSX.Element[] = [];

        const showWarningMsg = this.props.infoMessage && (this.props.disableSelection || this.props.disabledFormFields);
        if (showWarningMsg)
            render.push(<p key="warning-msg" style={{ textAlign: "center", fontStyle: "italic", padding: "0 15px" }}>{this.props.infoMessage}</p>);

        const itemWillBeUnlinked = this.props.linkedElement
            && this.props.linkedElement.linkAddedNewEntry
            && (
                !this.state.selectedElement
                || this.props.linkedElement.model.EMRTemplateId !== this.state.selectedElement.model.EMRTemplateId
            );

        render.push(
            <MedicalRequestFormRowComponent label="ItemLabel" dataCy="itemRow" key="itemWillBeUnlinkedMessageRow">
                {this.renderSelectableCareElement(elements)}
                {itemWillBeUnlinked && (
                    <p style={{ padding: "7px 0" }}>
                        <strong style={{ "color": "red", fontStyle: "italic" }}>
                            {cacheStore.GetString("UnlinkCareElementWarningLabel")}
                        </strong>
                    </p>
                )}
            </MedicalRequestFormRowComponent>
        );

        render.push(this.renderScreeningSection(this.state.selectedElement, "screening-section"));

        return render;
    }

    private renderSelectableCareElement(elements: MedicalItemScreeningTest[]): JSX.Element {
        const selectedTest = this.state.selectedElement && elements.firstOrDefault(p => p.model.MedicalItemId === this.state.selectedElement.model.MedicalItemId);

        if (this.props.disableSelection) {
            return (
                <div key="selectableCareElement">
                    {selectedTest?.getScreeningTestTitle() ?? ""}
                </div>
            );
        }

        const sorted = elements.sortByProperty(p => p.model.EMRTemplateIsInvalid);
        const invalidClass = selectedTest?.model.EMRTemplateIsInvalid ? "invalid-linked-element" : "";

        return (
            <select
                key="careElementDropdownSelectorKey"
                className={`form-control myle-control ${invalidClass}`}
                onChange={(e: any) => this.handleOnChange(e.target.value)}
                value={this.state.selectedElement ? this.state.selectedElement.model.MedicalItemId : ""}
                disabled={this.props.disableSelection}
            >
                <option key="care-element-0" value="" />
                {sorted.map((x: MedicalItemScreeningTest) => {
                    const testDefinitionName = x.getScreeningTestTitle();
                    const optionText =
                        (
                            x.model.EMRTemplateIsInvalid
                                ? `[${cacheStore.GetString("InvalidMedicalItemLabel")}] `
                                : ""
                        )
                        + (
                            testDefinitionName
                                ? limitTextLength(testDefinitionName, 83)
                                : ""
                        );
                    return (
                        <option
                            key={x.model.MedicalItemId}
                            title={testDefinitionName}
                            value={x.model.MedicalItemId}
                            className={x.model.EMRTemplateIsInvalid ? "invalid-linked-element" : "valid-linked-element"}
                        >
                            {optionText}
                        </option>
                    );
                })}
            </select>
        );
    }

    private renderScreeningSection(screeningTest: MedicalItemScreeningTest, key: string): JSX.Element {
        if (!screeningTest)
            return null;

        if (this.props.disabledFormFields) {
            return (
                <ScreeningTestViewComponent
                    key={key}
                    screeningTest={screeningTest}
                />
            );
        }

        return (
            <ScreeningTestFormComponent
                key={key}
                model={screeningTest}
                patientDoB={this.props.patientDoB}
                disabled={this.props.disabledFormFields}

                onFrequencyChanged={dropdownId => this.mutateModelState(m => m.model.FrequencyTypeDropDownId = dropdownId)}
                onReminderChanged={dropdownId => this.mutateModelState(m => m.model.ReminderTypeDropDownId = dropdownId)}
                onStartAgeChanged={startAge => this.mutateModelState(m => m.model.StartAge = startAge)}
                onEndAgeChanged={endAge => this.mutateModelState(m => m.model.EndAge = endAge)}
                onStatusChanged={lookupId => this.onStatusChange(lookupId)}
                onCompletionDateChanged={date => this.mutateModelState(m => m.model.CompletionDate = date)}
                onInterpretationChanged={lookupId => this.mutateModelState(m => m.model.InterpretationLookupId = lookupId)}
                onDueDateChanged={date => this.mutateModelState(m => m.model.DueDate = date)}
                onNotesChanged={notes => this.mutateModelState(m => m.model.Notes = notes)}
            />
        );
    }

    private handleOnChange(medicalItemId: string): void {
        this.mutateState(
            s => {
                s.selectedElement = this.props.elements.firstOrDefault(x => x.model.MedicalItemId === medicalItemId);
                if (this.props.shouldSetSelectElementToPendingStatus && s.selectedElement) {
                    s.selectedElement.model.StatusLookupId = ScreeningTestStatus.Pending.LookupID;
                    s.selectedElement.model.InterpretationLookupId = "";
                    s.selectedElement.model.CompletionDate = null;
                }
            },
            () => this.props.onSelectedElementChanged && this.props.onSelectedElementChanged(this.state.selectedElement)
        );
    }

    private onStatusChange(statusLookupId: string): void {
        this.mutateModelState(m => {
            m.model.StatusLookupId = statusLookupId;
            if (!MedicalItemScreeningTest.isCompleted(statusLookupId)) {
                m.model.InterpretationLookupId = "";
                m.model.CompletionDate = null;
            }
        });
    }
}

export { LinkCareElementComponent };
